import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * The page container, which was hand-written ~30 times at eight different widths
 * (`max-w-md` ×12, `max-w-4xl` ×9, `max-w-lg` ×8, `max-w-3xl` ×7, plus one-offs
 * at 5xl/6xl/7xl/xl). Inconsistent measure is most of why the app reads as a set
 * of unrelated screens rather than one product.
 *
 * Four widths, named by what they are for rather than by size, so the choice is
 * a decision about content instead of a number someone copied from next door.
 */
const pageShell = cva("mx-auto w-full px-4 sm:px-6", {
  variants: {
    width: {
      /** Auth and single-column forms. Replaces max-w-md / max-w-lg. */
      form: "max-w-md",
      /** Long-form reading — lesson text, policy pages. */
      prose: "max-w-3xl",
      /** Default application content. Replaces max-w-3xl/4xl/5xl. */
      app: "max-w-5xl",
      /** Dashboards, tables, the player. */
      wide: "max-w-7xl",
    },
    pad: {
      none: "",
      tight: "py-6",
      base: "py-10",
      loose: "py-16",
    },
  },
  defaultVariants: { width: "app", pad: "base" },
});

type PageShellProps = React.ComponentPropsWithoutRef<"main"> &
  VariantProps<typeof pageShell> & {
    /** Render as a plain block instead of <main> — for nested sections. */
    as?: "main" | "div" | "section";
  };

export function PageShell({ as = "main", width, pad, className, ...props }: PageShellProps) {
  const Tag = as;
  return <Tag className={cn(pageShell({ width, pad }), className)} {...props} />;
}
